import { Fragment, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Flex, Grid, Image, Link, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ACCENT,
  BG,
  BLUE,
  BODY,
  EASE,
  FG,
  GOLD,
  GRID,
  HEAT,
  INSET,
  MONO,
  MUTED,
  PANEL,
  SERIF,
  alpha,
} from "../theme";
import { ArrowLink, Card, Container, Em, Eyebrow, Mono, Reveal, SectionHead } from "../components/ui";
import NowCard from "../components/NowCard";
import FormGap from "../viz/FormGap";
import { HoverGif } from "../components/Media";
import { useFitness, useJournal, useTennis } from "../data/useJson";
import { EXTERNAL, PROJECTS } from "../data/projects";
import { prepare } from "../journal/journal";
import Masonry from "../journal/Masonry";
import { HeatmapSvg } from "../fitness/ui";
import { withLoads } from "../fitness/model";

const MotionSpan = motion.span;

// headline that rises in word by word
// (real spaces between the words, so screen readers and copy-paste get a sentence)
const RiseWords = ({ words, delay = 0 }) =>
  words.map((w, i) => (
    <Fragment key={i}>
      <Box as="span" display="inline-block" overflow="hidden" verticalAlign="top" pb="0.08em">
        <MotionSpan
          style={{ display: "inline-block" }}
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: delay + i * 0.07, ease: EASE }}
        >
          {w}
        </MotionSpan>
      </Box>{" "}
    </Fragment>
  ));

// the many ways to finish "I …" — all true, cycled slowly
const FACETS = [
  "build drones for a living.",
  "play a lot of left-handed tennis.",
  "log every match, run and night of sleep.",
  "keep a tennis nemesis.",
  "wing-foil the bay when it’s windy.",
  "write things down.",
];

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Facets = ({ facets }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || reduced()) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % facets.length), 2800);
    return () => clearInterval(t);
  }, [paused, facets.length]);
  return (
    <Text
      as="p"
      fontFamily={SERIF}
      fontSize={{ base: "32px", md: "44px", xl: "50px" }}
      lineHeight="1.15"
      letterSpacing="-0.01em"
      fontWeight={400}
      color={MUTED}
      mt={{ base: 4, md: 5 }}
      minH={{ base: "2.3em", lg: "1.15em" }}
      aria-label={`I ${facets.join(" I ")}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Box as="span" aria-hidden="true">
        I{" "}
        <Box as="span" display="inline-grid" verticalAlign="top" overflow="hidden" minH="1.2em">
          <AnimatePresence mode="wait" initial={false}>
            <MotionSpan
              key={i}
              style={{ gridArea: "1 / 1", color: FG }}
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "-100%", opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {facets[i]}
            </MotionSpan>
          </AnimatePresence>
        </Box>
      </Box>
    </Text>
  );
};

const Ticker = ({ data, tennis }) => {
  const items = [
    "Drones at Vantage Robotics",
    "Lefty forehand",
    data && `${data.totals.hours} hours logged`,
    "Wing-foiling the bay",
    "Dishcare co-founder",
    tennis && tennis.nemesis && `Nemesis: ${tennis.nemesis}`,
    data && `${data.totals.km.toLocaleString()} km tracked`,
    "Purdue M.S.",
    "2 patents",
    "logtennis.net",
    "CHI · DIS · ASME JMD",
    data && `${data.totals.activities} sessions`,
  ].filter(Boolean);
  const row = (
    <Flex gap={10} pr={10} flexShrink={0} align="center">
      {items.map((t) => (
        <Flex key={t} align="center" gap={10}>
          <Text fontFamily={MONO} fontSize="sm" letterSpacing="0.08em" textTransform="uppercase" color={BODY} whiteSpace="nowrap">
            {t}
          </Text>
          <Text color={ACCENT} fontSize="xs">✦</Text>
        </Flex>
      ))}
    </Flex>
  );
  return (
    <Box borderY={`1px solid ${GRID}`} py={4} overflow="hidden" position="relative"
      sx={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
      <Flex w="max-content" sx={{ animation: "marquee 52s linear infinite" }} _hover={{ animationPlayState: "paused" }}>
        {row}
        {row}
      </Flex>
    </Box>
  );
};

const STATS = [
  ["10+", "years building hardware", ACCENT],
  ["5", "industries shipped in", BLUE],
  ["2", "patents filed", GOLD],
  ["100s", "of drones out the door monthly", HEAT],
];

const ProjectTile = ({ p, big, i }) => (
  <Reveal delay={i * 0.08} h="100%">
    <Link
      as={p.href ? undefined : RouterLink}
      to={p.href ? undefined : `/work/${p.slug}`}
      href={p.href}
      isExternal={!!p.href}
      display="flex"
      flexDirection="column"
      h="100%"
      bg={PANEL}
      border={`1px solid ${GRID}`}
      borderRadius="2xl"
      overflow="hidden"
      role="group"
      _hover={{ textDecoration: "none", borderColor: alpha(p.color, 0.5) }}
      transition="border-color .3s"
    >
      <Box
        position="relative"
        flex={big ? "1" : undefined}
        h={big ? { base: "260px", lg: "auto" } : { base: "220px", md: "240px" }}
        minH={big ? { lg: "420px" } : undefined}
        overflow="hidden"
        bg={INSET}
        sx={{ "& img": { transition: "transform 1s cubic-bezier(0.22, 1, 0.36, 1)" }, "&:hover img": { transform: "scale(1.04)" } }}
      >
        <HoverGif src={p.hover} poster={p.cover} alt={p.name} fit={p.contain ? "contain" : "cover"} p={p.contain ? 6 : 0} />
        <Flex position="absolute" top={4} left={4} gap={2}>
          <Box px={2.5} py={1} borderRadius="full" bg={alpha(BG, 0.75)} backdropFilter="blur(6px)">
            <Mono color={p.color} fontSize="10px">
              {p.role}
            </Mono>
          </Box>
        </Flex>
      </Box>
      <Box p={{ base: 5, md: 6 }}>
        <Flex justify="space-between" align="baseline" gap={3}>
          <Text color={FG} fontSize={big ? { base: "2xl", md: "4xl" } : "2xl"} fontWeight={600} letterSpacing="-0.04em" lineHeight="1">
            {p.name}
          </Text>
          <Text color={MUTED} fontSize="xl" transition="all .3s" _groupHover={{ color: p.color, transform: "translate(3px,-3px)" }}>
            {p.href ? "↗" : "→"}
          </Text>
        </Flex>
        <Text color={BODY} fontFamily={SERIF} fontStyle="italic" fontSize={big ? "2xl" : "xl"} mt={2} lineHeight="1.2">
          {p.kicker}
        </Text>
        {big && (
          <Text color={MUTED} fontSize="sm" mt={3} lineHeight="1.7" maxW="560px">
            {p.summary}
          </Text>
        )}
      </Box>
    </Link>
  </Reveal>
);

// a compact fitness-vs-fatigue chart for the teaser
// fitness vs fatigue for the last four months; the shaded gap is form
const EngineTeaser = ({ data }) => {
  const rows = useMemo(() => withLoads(data.fitness).slice(-120), [data]);
  return <FormGap rows={rows} height={320} width={640} id="home-fg" />;
};

const HomePage = () => {
  const fitness = useFitness();
  const tennis = useTennis();
  const journalJson = useJournal();
  const journal = useMemo(() => prepare(journalJson), [journalJson]);
  const latest = journal.slice(0, 6);
  // the "thinking" line on the status card: newest thought, else newest quote
  const note = journal.find((e) => e.type === "thought") || journal.find((e) => e.type === "quote");
  const featured = [PROJECTS[0], PROJECTS[1], PROJECTS[2], EXTERNAL[0]];

  return (
    <Box>
      {/* ---------- hero ---------- */}
      <Box position="relative" overflow="hidden">
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          sx={{
            backgroundImage: `linear-gradient(${alpha(FG, 0.035)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(FG, 0.035)} 1px, transparent 1px)`,
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse 70% 60% at 70% 40%, #000 30%, transparent 75%)",
          }}
        />
        <Box position="absolute" w="900px" h="900px" right="-200px" top="-300px" borderRadius="full" pointerEvents="none"
          bg={`radial-gradient(circle, ${alpha(ACCENT, 0.09)}, transparent 60%)`} />
        <Container position="relative" pt={{ base: 10, md: 16, lg: 20 }} pb={{ base: 14, md: 20 }}>
          <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 1.35fr) minmax(0, 1fr)" }} gap={{ base: 12, lg: 14 }} alignItems="center">
            <Box>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
                <Eyebrow index="P.C.">SF Bay Area · engineer · lefty</Eyebrow>
              </motion.div>
              <Text as="h1" fontSize={{ base: "60px", sm: "80px", md: "104px", xl: "128px" }} lineHeight="0.95"
                letterSpacing="-0.055em" fontWeight={600} color={FG}>
                <RiseWords words={["Hi,", "I’m"]} />
                <Box as="span" display="inline-block" overflow="hidden" verticalAlign="top" pb="0.1em">
                  <MotionSpan
                    style={{ display: "inline-block" }}
                    initial={{ y: "110%", rotate: 4 }}
                    animate={{ y: 0, rotate: 0 }}
                    transition={{ duration: 1, delay: 0.22, ease: EASE }}
                  >
                    <Em letterSpacing="-0.03em" pr="0.06em">Pratik.</Em>
                  </MotionSpan>
                </Box>
              </Text>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55, ease: EASE }}>
                <Facets facets={FACETS} />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75, ease: EASE }}>
                <Text color={BODY} mt={{ base: 7, md: 9 }} maxW="560px" fontSize={{ base: "md", md: "lg" }} lineHeight="1.7">
                  By day I work on drones at{" "}
                  <Text as="span" color={FG} fontWeight={600}>Vantage Robotics</Text>. The rest of the time I’m on a
                  tennis court, out on the bay, or writing things down. This is where all of it lives — the
                  things I’ve built, every session straight from my watch, and a journal of what’s on my mind.
                </Text>
                <Flex gap={3} mt={9} wrap="wrap">
                  <Button as={RouterLink} to="/journal" variant="volt" size="lg" px={7}>
                    Read the journal →
                  </Button>
                  <Button as={RouterLink} to="/work" variant="line" size="lg" px={7}>
                    See the work
                  </Button>
                </Flex>
              </motion.div>
            </Box>

            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5, ease: EASE }}>
              <Box position="relative">
                {fitness && fitness.heatmap && (
                  <Box position="absolute" inset="-18%" opacity={0.5} pointerEvents="none" zIndex={0}>
                    <HeatmapSvg tracks={fitness.heatmap} />
                  </Box>
                )}
                <Box position="relative" zIndex={1}>
                  {fitness ? (
                    <NowCard fitness={fitness} tennis={tennis || null} journal={note ? [note] : []} />
                  ) : (
                    <Box h="440px" borderRadius="2xl" border={`1px solid ${GRID}`} bg={PANEL} />
                  )}
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Container>
      </Box>

      <Ticker data={fitness} tennis={tennis} />

      {/* ---------- journal ---------- */}
      {latest.length > 0 && (
        <Container mt={{ base: 20, md: 28 }}>
          <SectionHead
            index="01"
            eyebrow="Journal"
            title={<>Lately, in <Em>pictures & words.</Em></>}
            right={<ArrowLink to="/journal">Every field note</ArrowLink>}
          />
          <Masonry entries={latest} />
        </Container>
      )}

      {/* ---------- training ---------- */}
      {fitness && fitness.fitness && fitness.fitness.length > 10 && (
        <Container mt={{ base: 20, md: 28 }}>
          <Reveal>
            <Card p={{ base: 6, md: 10 }} position="relative" overflow="hidden">
              <Box position="absolute" right="-120px" top="-120px" w="420px" h="420px" borderRadius="full"
                bg={`radial-gradient(circle, ${alpha(ACCENT, 0.1)}, transparent 65%)`} pointerEvents="none" />
              <Grid templateColumns={{ base: "1fr", lg: "1fr 1.3fr" }} gap={{ base: 10, lg: 14 }} alignItems="center">
                <Box>
                  <Eyebrow index="02">Off the clock</Eyebrow>
                  <Text as="h2" color={FG} fontSize={{ base: "34px", md: "52px" }} fontWeight={600} letterSpacing="-0.045em" lineHeight="1">
                    Same obsession with data, pointed at <Em>my own engine.</Em>
                  </Text>
                  <Text color={BODY} mt={6} lineHeight="1.7">
                    Tennis, soccer, runs and rides — every session streams from my Garmin into a
                    dashboard I built. The bright line is my fitness, the orange line is how tired
                    I am, and the gap between them is my form. There’s a whole page on how that
                    math works, with a planner you can play with.
                  </Text>
                  <Flex gap={3} mt={8} wrap="wrap">
                    <Button as={RouterLink} to="/fitness" variant="volt">
                      Enter the training log →
                    </Button>
                    <Button as={RouterLink} to="/fitness/engine" variant="line">
                      How the engine works
                    </Button>
                  </Flex>
                </Box>
                <Box>
                  <Flex justify="space-between" mb={3}>
                    <Mono>Last 120 days</Mono>
                    <Mono color={ACCENT}>{fitness.totals.hours} h · {fitness.totals.activities} sessions</Mono>
                  </Flex>
                  <EngineTeaser data={fitness} />
                </Box>
              </Grid>
            </Card>
          </Reveal>
        </Container>
      )}

      {/* ---------- work ---------- */}
      <Container mt={{ base: 20, md: 28 }}>
        <SectionHead
          index="03"
          eyebrow="The day job, and the ones before it"
          title={<>Things I’ve <Em>built.</Em></>}
          right={<ArrowLink to="/work">All projects</ArrowLink>}
        />
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "1.25fr 1fr 1fr" }} templateRows={{ lg: "auto auto" }} gap={5}>
          <Box gridRow={{ lg: "span 2" }}>
            <ProjectTile p={featured[0]} big i={0} />
          </Box>
          {featured.slice(1).map((p, i) => (
            <Box key={p.name} gridColumn={{ lg: i === 2 ? "span 2" : undefined }}>
              <ProjectTile p={p} i={i + 1} />
            </Box>
          ))}
        </Grid>

        <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} mt={5} border={`1px solid ${GRID}`} borderRadius="2xl" overflow="hidden">
          {STATS.map(([v, l, c], i) => (
            <Reveal key={l} delay={i * 0.06} p={{ base: 5, md: 7 }}
              borderRight={{ base: i % 2 ? "none" : `1px solid ${GRID}`, md: i < 3 ? `1px solid ${GRID}` : "none" }}
              borderBottom={{ base: i < 2 ? `1px solid ${GRID}` : "none", md: "none" }}>
              <Text color={FG} fontSize={{ base: "4xl", md: "6xl" }} fontWeight={600} letterSpacing="-0.05em" lineHeight="1">
                {v}
              </Text>
              <Flex align="center" gap={2} mt={3}>
                <Box w="6px" h="6px" borderRadius="full" bg={c} />
                <Mono>{l}</Mono>
              </Flex>
            </Reveal>
          ))}
        </Grid>
      </Container>

      {/* ---------- portrait strip ---------- */}
      <Container mt={{ base: 20, md: 28 }}>
        <Reveal>
          <Grid templateColumns={{ base: "1fr", md: "auto 1fr auto" }} gap={{ base: 6, md: 10 }} alignItems="center">
            <Image src="/pratik-portrait.jpg" alt="Pratik Chawla" boxSize={{ base: "88px", md: "112px" }} borderRadius="full" objectFit="cover"
              border={`2px solid ${ACCENT}`} bg={PANEL} />
            <Text fontFamily={SERIF} fontSize={{ base: "28px", md: "40px" }} lineHeight="1.15" color={FG}>
              “Let’s chat about cool projects or epic sports moments — <Text as="span" color={ACCENT} fontStyle="italic">I’m all ears.</Text>”
            </Text>
            <ArrowLink to="/about">The long version</ArrowLink>
          </Grid>
        </Reveal>
      </Container>
    </Box>
  );
};

export default HomePage;
